import React, { useState, useEffect } from 'react';

function ThemePanel() {
	const [mode, setMode] = useState(localStorage?.appMode === 'dark');
	const [theme, setTheme] = useState(localStorage?.appTheme || 'theme-primary');
	const [rtlEnabled, setRtlEnabled] = useState(localStorage?.rtlEnabled === 'true');
	const [active, setActive] = useState(() => {
		if (typeof window !== 'undefined') {
			return localStorage.getItem('themePanelExpand') === 'true';
		}
		return false;
	});

	const toggleAppThemePanel = (event) => {
		event.preventDefault();
		const newActive = !active;
		setActive(newActive);
		localStorage.setItem('themePanelExpand', newActive.toString());
	};

	const setAppTheme = (event, theme) => {
		event.preventDefault();

		// Remove previous theme class
		document.body.classList.forEach((cls) => {
			if (cls.startsWith('theme-') && cls !== theme) {
				document.body.classList.remove(cls);
			}
		});

		// Apply new theme
		if (theme) {
			document.body.classList.add(theme);
			localStorage.setItem('appTheme', theme);
			setTheme(theme);
			document.dispatchEvent(new Event('theme-reload'));
		}
	};

	const setAppMode = (mode) => {
		document.documentElement.setAttribute('data-bs-theme', mode);
		localStorage.setItem('appMode', mode);
		document.dispatchEvent(new Event('theme-reload'));
	};

	const handleCheckboxChange = (event) => {
		const isChecked = event.target.checked;
		setMode(isChecked);
		setAppMode(isChecked ? 'dark' : 'light');
	};

	const handleRtlToggle = (e) => {
		const isEnabled = e.target.checked;
		setRtlEnabled(isEnabled);
		if (isEnabled) {
			document.documentElement.setAttribute('dir', 'rtl');
		} else {
			document.documentElement.removeAttribute('dir');
		}
		localStorage.setItem('rtlEnabled', isEnabled.toString());
	};

	const themeList = [
		{ name: 'Pink', bgClass: 'bg-pink', themeClass: 'theme-pink' },
		{ name: 'Red', bgClass: 'bg-red', themeClass: 'theme-red' },
		{ name: 'Orange', bgClass: 'bg-warning', themeClass: 'theme-warning' },
		{ name: 'Yellow', bgClass: 'bg-yellow', themeClass: 'theme-yellow' },
		{ name: 'Lime', bgClass: 'bg-lime', themeClass: 'theme-lime' },
		{ name: 'Green', bgClass: 'bg-green', themeClass: 'theme-green' },
		{ name: 'Default', bgClass: 'bg-teal', themeClass: 'theme-teal' },
		{ name: 'Cyan', bgClass: 'bg-info', themeClass: 'theme-info' },
		{ name: 'Blue', bgClass: 'bg-primary', themeClass: 'theme-primary' },
		{ name: 'Purple', bgClass: 'bg-purple', themeClass: 'theme-purple' },
		{ name: 'Indigo', bgClass: 'bg-indigo', themeClass: 'theme-indigo' },
		{ name: 'Gray', bgClass: 'bg-gray-200', themeClass: 'theme-gray-200' }
	];

	useEffect(() => {
		// Set initial theme
		setTimeout(() => {
			const storedTheme = localStorage.getItem('appTheme') || 'theme-teal';
			document.body.classList.add(storedTheme);
			setTheme(storedTheme);
		}, 0);

		// Set initial mode
		const savedMode = localStorage.getItem('appMode');
		if (savedMode === 'dark') {
			document.documentElement.setAttribute('data-bs-theme', 'dark');
			setMode(true);
		} else {
			document.documentElement.setAttribute('data-bs-theme', 'light');
			setMode(false);
		}

		// Set initial RTL
		if (rtlEnabled) {
			document.documentElement.setAttribute('dir', 'rtl');
		}
		
		// eslint-disable-next-line
	}, []);

	return (
		<div className={'theme-panel' + (active ? ' active' : '')}>
			<a href="#/" onClick={toggleAppThemePanel} className="theme-collapse-btn">
				<i className="fa fa-cog"></i>
			</a>
			<div className="theme-panel-content">
				<ul className="theme-list clearfix">
					{themeList.map((t, index) => (
						<li className={theme === t.themeClass ? 'active' : ''} key={index}>
							<a href="#/" onClick={(e) => setAppTheme(e, t.themeClass)} className={t.bgClass}>&nbsp;</a>
						</li>
					))}
				</ul>

				<hr className="mb-0" />
				<div className="row mt-10px pt-3px">
					<div className="col-9 control-label text-body-emphasis fw-bold">
						<div class="d-flex">Dark Mode <span className="badge bg-theme text-theme-color ms-1 d-flex align-items-center">NEW</span></div>
						<div className="lh-sm fs-13px fw-semibold">
							<small className="text-body-emphasis opacity-50">
								Adjust the appearance to reduce glare and give your eyes a break.
							</small>
						</div>
					</div>
					<div className="col-3 d-flex">
						<div className="form-check form-switch ms-auto mb-0 mt-2px">
							<input type="checkbox" className="form-check-input" onChange={handleCheckboxChange} checked={mode} />
							<label className="form-check-label" htmlFor="appThemeDarkMode">&nbsp;</label>
						</div>
					</div>
				</div>

				<hr className="mb-0" />
				<div className="row mt-10px pt-3px">
					<div className="col-9">
						<div className="fw-bold text-body d-flex">
							RTL Support <span className="badge bg-theme text-theme-color ms-1 d-flex align-items-center">NEW</span>
						</div>
						<div className="lh-sm fs-13px fw-semibold">
							<small className="text-body-emphasis opacity-50">
								Easily switch to right-to-left layout for Arabic, Hebrew, and other RTL languages.
							</small>
						</div>
					</div>
					<div className="col-3 d-flex">
						<div className="form-check form-switch ms-auto mb-0">
							<input type="checkbox" className="form-check-input" id="appThemeRtl" checked={rtlEnabled} onChange={handleRtlToggle} />
							<label className="form-check-label" htmlFor="appThemeRtl"></label>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default ThemePanel;